import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import * as S from './styles'

interface HorizontalScrollProps {
  children: ReactNode
  className?: string
  label?: string
}

export function HorizontalScroll({ children, className, label = 'Tabela com rolagem lateral' }: HorizontalScrollProps): ReactElement {
  const contentRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ scroll: 0, client: 0 })

  useEffect(() => {
    const content = contentRef.current
    if (content == null) return undefined
    const measure = (): void => setSize({ scroll: content.scrollWidth, client: content.clientWidth })
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    if (content.firstElementChild != null) observer.observe(content.firstElementChild)
    return () => observer.disconnect()
  }, [])

  const overflowing = size.scroll > size.client + 1

  useEffect(() => {
    if (overflowing && barRef.current != null && contentRef.current != null) {
      barRef.current.scrollLeft = contentRef.current.scrollLeft
    }
  }, [overflowing])

  const follow = (source: 'content' | 'bar'): void => {
    const content = contentRef.current
    const bar = barRef.current
    if (content == null || bar == null) return
    if (source === 'content' && bar.scrollLeft !== content.scrollLeft) bar.scrollLeft = content.scrollLeft
    if (source === 'bar' && content.scrollLeft !== bar.scrollLeft) content.scrollLeft = bar.scrollLeft
  }

  return (
    <S.Wrapper className={className}>
      <S.Content
        ref={contentRef}
        data-horizontal-scroll=""
        data-testid="horizontal-scroll-content"
        onScroll={() => follow('content')}
        {...(overflowing ? { tabIndex: 0, role: 'region', 'aria-label': label } : {})}
      >
        {children}
      </S.Content>
      {overflowing && (
        <S.Bar ref={barRef} aria-hidden="true" data-testid="horizontal-scroll-bar" onScroll={() => follow('bar')}>
          <S.Spacer style={{ width: size.scroll }} />
        </S.Bar>
      )}
    </S.Wrapper>
  )
}
